import React, { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { cn } from "../lib/utils";

// Options may be plain strings OR objects: { value, label?, eid?, sub? }
const optVal = (o) => (typeof o === "string" ? o : o.value);
const optLabel = (o) => (typeof o === "string" ? o : (o.label ?? o.value));
const optEid = (o) => (typeof o === "string" ? "" : (o.eid || ""));

export function Combobox({
  value,
  valueEid = null,
  onChange,
  options = [],
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  allowCreate = false,
  onCreate,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const norm = (s) => (s || "").trim().toLowerCase();
  const exists = options.some((o) => norm(optVal(o)) === norm(query));
  const showCreate = allowCreate && query.trim() && !exists;

  const pick = (val, opt) => { onChange(val, opt); setOpen(false); setQuery(""); };

  const create = async () => {
    const val = query.trim();
    if (!val) return;
    if (onCreate) { try { await onCreate(val); } catch (e) { /* handled upstream */ } }
    pick(val, undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testid}
          className="w-full justify-between rounded-sm font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value ? (
              <>
                {value}
                {valueEid ? <span className="text-muted-foreground"> · EID {valueEid}</span> : null}
              </>
            ) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card" align="start">
        <Command shouldFilter={true}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery}
            data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            {!showCreate && <CommandEmpty>No match found.</CommandEmpty>}
            <CommandGroup>
              {options.map((opt, i) => {
                const val = optVal(opt);
                const eid = optEid(opt);
                const selected = value === val && (!valueEid || valueEid === eid);
                return (
                  <CommandItem
                    key={`${val}-${eid || i}`}
                    value={`${optLabel(opt)} ${eid}`}
                    onSelect={() => pick(val, typeof opt === "string" ? undefined : opt)}
                    data-testid={testid ? `${testid}-option-${eid || val}` : undefined}
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                    <span className="flex-1 truncate">{optLabel(opt)}</span>
                    {eid ? <span className="ml-2 shrink-0 text-xs text-muted-foreground font-mono">EID {eid}</span> : null}
                  </CommandItem>
                );
              })}
              {showCreate && (
                <CommandItem value={`__create__${query}`} onSelect={create}
                  data-testid={testid ? `${testid}-create` : undefined}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add “{query.trim()}”
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
